// src/context/AppContext.jsx

import React, { createContext, useState, useContext, useEffect } from 'react';

// Helper functions to get/set from localStorage
const getStoredJSON = (key, defaultValue) => {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
};
const setStoredJSON = (key, value) => {
    localStorage.setItem(key, JSON.stringify(value));
};

const AppContext = createContext();

export const AppProvider = ({ children }) => {
    // State for navigation
    const [currentScreen, setCurrentScreen] = useState('screen-1');

    // State for app data, initialized from localStorage
    const [userInfo, setUserInfo] = useState(() => getStoredJSON('userInfo', null));
    
    // NEW state for rankings
    const [rankingsTestDrive, setRankingsTestDrive] = useState(() => getStoredJSON('carRankings-testDrive', []));
    const [rankingsDesign, setRankingsDesign] = useState(() => getStoredJSON('carRankings-design', []));
    const [rankingsInterior, setRankingsInterior] = useState(() => getStoredJSON('carRankings-interior', []));
    
    // State for quiz
    const [quizAnswers, setQuizAnswers] = useState({ q1: '', q2a: '', q2b: '' });
    const [quizConsent, setQuizConsent] = useState(false);

    // --- Effects to persist data to localStorage ---
    useEffect(() => {
        if (userInfo) {
            setStoredJSON('userInfo', userInfo);
        } else {
            localStorage.removeItem('userInfo');
        }
    }, [userInfo]);

    // NEW effects for rankings
    useEffect(() => {
        setStoredJSON('carRankings-testDrive', rankingsTestDrive);
    }, [rankingsTestDrive]);

    useEffect(() => {
        setStoredJSON('carRankings-design', rankingsDesign);
    }, [rankingsDesign]);

    useEffect(() => {
        setStoredJSON('carRankings-interior', rankingsInterior);
    }, [rankingsInterior]);


    // --- Core Functions ---
    const showScreen = (screenId) => {
        // Data clearing logic from new JS
        if (screenId === 'screen-8-thankyou') {
            // Wipe all state (localStorage is cleared by API success or on next reload)
            // We also clear state here to be safe.
            setUserInfo(null);
            setRankingsTestDrive([]);
            setRankingsDesign([]);
            setRankingsInterior([]);
            setQuizAnswers({ q1: '', q2a: '', q2b: '' });
            setQuizConsent(false);
            
            // Clear localStorage *after* submission
            localStorage.removeItem('userInfo');
            localStorage.removeItem('carRankings-testDrive');
            localStorage.removeItem('carRankings-design');
            localStorage.removeItem('carRankings-interior');
            
            console.log('Local storage cleared. Session complete.');
        } else if (screenId === 'screen-1') {
            // If navigating to screen 1 (e.g. on Thank You)
            // Reset quiz/ranking state
            setRankingsTestDrive([]);
            setRankingsDesign([]);
            setRankingsInterior([]);
            setQuizAnswers({ q1: '', q2a: '', q2b: '' });
            setQuizConsent(false);
        }
        setCurrentScreen(screenId);
    };

    // NEW generic function to update a ranking
    const updateRankings = (key, carId) => {
        const rankingsMap = {
            testDrive: [rankingsTestDrive, setRankingsTestDrive],
            design: [rankingsDesign, setRankingsDesign],
            interior: [rankingsInterior, setRankingsInterior],
        };

        const [currentRankings, setter] = rankingsMap[key];
        
        const newRankings = [...currentRankings];
        const existingRankIndex = newRankings.indexOf(carId);

        if (existingRankIndex > -1) {
            newRankings.splice(existingRankIndex, 1); // Remove
        } else {
            newRankings.push(carId); // Add
        }
        
        setter(newRankings);
    };


    // Value to be provided to all components
    const value = {
        currentScreen,
        showScreen,
        userInfo,
        setUserInfo,
        
        // Pass new state and update function
        rankingsTestDrive,
        rankingsDesign,
        rankingsInterior,
        updateRankings,
        
        quizAnswers,
        setQuizAnswers,
        quizConsent,
        setQuizConsent,
    };

    return (
        <AppContext.Provider value={value}>
            {children}
        </AppContext.Provider>
    );
};

// Custom hook to easily consume the context
export const useAppContext = () => {
    return useContext(AppContext);
};